import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useTranslation } from "react-i18next";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import { useAuth } from "@context/AuthContext";
import { useHomeSummary } from "@hooks/useHomeSummary";
import { PAGE_CONTAINER_SX } from "@/constants/layout";
import { HOME_FEATURE_CARDS, HOME_STAT_BADGES } from "@/constants/home";

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const summary = useHomeSummary(isAuthenticated);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        ...PAGE_CONTAINER_SX,
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 2,
          p: { xs: 3, sm: 5 },
          position: "relative",
          overflow: "hidden",
          mb: 4,
          color: isDark ? theme.palette.grey[50] : "#FFFFFF",
          background: isDark
            ? `linear-gradient(132deg, ${theme.palette.grey[900]} 0%, ${alpha(theme.palette.common.black, 0.5)} 28%, ${theme.palette.primary.dark} 62%, ${alpha("#020617", 0.95)} 100%)`
            : theme.palette.primary.main,
        }}
      >
        <SportsSoccerIcon
          sx={{
            position: "absolute",
            right: { xs: -30, md: 20 },
            bottom: { xs: -30, md: -20 },
            fontSize: { xs: 150, md: 200 },
            opacity: isDark ? 0.08 : 0.1,
            color: isDark ? theme.palette.common.white : "inherit",
          }}
        />
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.5rem", sm: "2.125rem" },
          }}
        >
          {t("home.welcome")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            opacity: isDark ? 0.88 : 0.92,
            mb: 3,
            maxWidth: 500,
            color: "inherit",
          }}
        >
          {t("home.subtitle")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {isAuthenticated ? (
            <>
              <Button
                variant="contained"
                color="inherit"
                sx={{
                  bgcolor: isDark ? theme.palette.background.paper : "#fff",
                  color: "primary.main",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDark ? theme.palette.grey[100] : "#fff",
                  },
                }}
                onClick={() => navigate("/teams")}
              >
                {t("home.newMatch")}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  color: "inherit",
                  borderColor: isDark
                    ? alpha("#fff", 0.45)
                    : alpha("#fff", 0.5),
                  "&:hover": {
                    borderColor: alpha("#fff", 0.85),
                    bgcolor: alpha("#fff", 0.06),
                  },
                }}
                onClick={() => navigate("/players")}
              >
                {t("home.managePlayers")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="inherit"
                sx={{
                  bgcolor: isDark ? theme.palette.background.paper : "#fff",
                  color: "primary.main",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDark ? theme.palette.grey[100] : "#fff",
                  },
                }}
                onClick={() => navigate("/account")}
              >
                {t("home.getStarted")}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  color: "inherit",
                  borderColor: isDark
                    ? alpha("#fff", 0.45)
                    : alpha("#fff", 0.5),
                  "&:hover": {
                    borderColor: alpha("#fff", 0.85),
                    bgcolor: alpha("#fff", 0.06),
                  },
                }}
                component={RouterLink}
                to="/preview"
              >
                {t("preview.cta")}
              </Button>
            </>
          )}
        </Box>
      </Box>
      {/* Stats cards (authenticated only) */}
      {isAuthenticated && summary && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "1fr 1fr 1fr 1fr",
            },
            gap: { xs: 2, sm: 3 },
            mb: 4,
          }}
        >
          {HOME_STAT_BADGES.map((stat) => (
            <Card
              key={stat.labelKey}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: alpha(stat.color, isDark ? 0.22 : 0.14),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <stat.icon sx={{ color: stat.color, fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    {t(stat.labelKey)}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      lineHeight: 1.3,
                    }}
                  >
                    {stat.getValue(summary)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      {/* Feature cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "1fr 1fr 1fr 1fr",
          },
          gap: 3,
        }}
      >
        {HOME_FEATURE_CARDS.map((f) => (
          <Card
            key={f.i18nTitle}
            sx={{
              cursor: "pointer",
              transition: "transform 0.15s, box-shadow 0.15s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 4,
              },
            }}
            onClick={() => navigate(f.path)}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: `${f.color}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <f.icon sx={{ fontSize: 28, color: f.color }} />
              </Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t(f.i18nTitle)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t(f.i18nDesc)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
